/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import Notify from "@stoneage/App/Components/Notify";
import requestSystemList from "@stoneage/App/Handlers/Data/RequestSystemListHandler";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaResourceManualEvent,
} from "@stoneage/App/Utilities/DataLayer";
import LazyImage, { LazyImagePresentationProps } from "@stoneage/MobiusOverrides/LazyImage";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    documents: state.xProductInfo.data?.productDocuments,
    isLoading: state.xProductInfo.isLoading,
    videoDocumentType: (getSettingsCollection(state) as any)?.customSettings["videoDocumentType"],
    customerNumber: getBillToCustomerNumber(state),
    userEmail: state.context.session.email,
    impersonation: trackAsImpersonation(state),
});

const mapDispatchToProps = {
    requestSystemList,
};

export interface DocumentListStlyes {
    container?: InjectableCss;
    documentLine?: InjectableCss;
    iconContainer?: InjectableCss;
    documentIcon?: LazyImagePresentationProps;
    documentTitleWrapper?: InjectableCss;
    title?: LinkPresentationProps;
    lastUpdatedLabel?: TypographyProps;
    lastUpdatedDate?: TypographyProps;
    description?: TypographyProps;
    descriptionTitleWrapper?: InjectableCss;
}

const styles: DocumentListStlyes = {
    container: {
        css: css`
            margin: 32px 0;
        `,
    },
    documentLine: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            padding-bottom: 13px;
            margin-bottom: 12px;
            border-bottom: 1px solid ${getColor("common.ui6")};
            @media (max-width: 600px) {
                align-items: flex-start;
            }
        `,
    },
    iconContainer: {
        css: css`
            width: 48px;
            height: 48px;
            margin-right: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    documentTitleWrapper: {
        css: css`
            width: calc(100% - 68px);
            display: flex;
            align-items: center;
            justify-content: space-between;
            @media (max-width: 600px) {
                flex-wrap: wrap;
                justify-content: flex-start;
            }
        `,
    },
    title: {
        color: "text.main",
        typographyProps: {
            size: "15px",
            lineHeight: "26px",
            weight: "bold",
        },
        css: css`
            flex-grow: 1;
            @media (max-width: 600px) {
                width: 100%;
                margin-bottom: 16px;
            }
        `,
    },
    lastUpdatedLabel: {
        size: "10px",
        lineHeight: "15px",
        css: css`
            margin-right: 15px;
        `,
    },
    lastUpdatedDate: {
        size: "12px",
        lineHeight: "18px",
        weight: "bold",
    },
    description: {
        size: "12px",
        lineHeight: "18px",
    },
    descriptionTitleWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ProductInformationDocumentList = ({
    requestSystemList,
    documents,
    isLoading,
    videoDocumentType,
    customerNumber,
    userEmail,
    impersonation,
}: Props) => {
    const [documentIcons, setDocumentIcons] = useState<{ name: string; description: string }[]>([]);
    useEffect(() => {
        requestSystemList({
            slug: "DocumentTypeIcon",
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    setDocumentIcons(resultProps.apiResult?.result?.values as { name: string; description: string }[]);
                }
            },
        });
    }, []);

    if (!isLoading && (!documents || !documents?.length)) {
        return (
            <StyledWrapper
                css={css`
                    height: 300px;
                    display: flex;
                    align-items: center;
                `}
            >
                <Notify messageType="info" body={translate("No documents found")} />
            </StyledWrapper>
        );
    }

    const getDocumentIcon = (type: string) => {
        return documentIcons?.filter(item => item.name === type)?.[0]?.description;
    };

    return (
        <StyledWrapper {...styles.container}>
            {documents &&
                documents.map(doc => (
                    <StyledWrapper key={uniqueId()} {...styles.documentLine}>
                        <StyledWrapper {...styles.iconContainer}>
                            <LazyImage
                                src={getDocumentIcon(doc.documentType)}
                                altText={doc.title}
                                height="48"
                                width="48"
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.documentTitleWrapper}>
                            <StyledWrapper {...styles.descriptionTitleWrapper}>
                                <Link
                                    href={doc.url}
                                    target="_blank"
                                    {...styles.title}
                                    onClick={() => {
                                        trackGaResourceManualEvent(doc.title, impersonation, userEmail, customerNumber);
                                    }}
                                >
                                    {doc.title}
                                </Link>
                                <Typography {...styles.description}>{doc.description}</Typography>
                            </StyledWrapper>

                            {doc.documentType.toLowerCase() !== videoDocumentType.toLowerCase() && (
                                <>
                                    <Typography {...styles.lastUpdatedLabel}>{translate("Updated")}</Typography>
                                    <Typography {...styles.lastUpdatedDate} translate="no">
                                        {doc.lastModifiedOn}
                                    </Typography>
                                </>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );
};

const widgetMoule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductInformationDocumentList),
    definition: {
        displayName: "Document List",
        group: "Product Information" as any,
        allowedContexts: ["ProductInformationPage"] as any,
    },
};

export default widgetMoule;
